/**
 * A name for the thing holding this passkey.
 *
 * ── Why the server cannot do this ────────────────────────────────────────────
 *
 * The server saw "This device" and "Another device", which is what a passkey
 * list looked like: three rows saying the same two words, and no way to tell
 * which one to remove when a phone is lost. That is worse than useless — it is
 * a security control nobody can operate.
 *
 * Only the browser knows what it is running on, and only roughly. So this makes
 * an honest guess and the person can correct it — the guess exists so that the
 * common case needs no typing, not so that it is always right.
 *
 * Deliberately coarse. `navigator.userAgent` can be made to say almost anything
 * and parsing it precisely is a losing game; "iPhone" and "Windows PC" are what
 * somebody needs to recognise their own device in a list of three.
 */
export function guessDeviceName(): string {
  if (typeof navigator === 'undefined') return 'This device'

  const ua = navigator.userAgent
  const platform =
    /iPhone/i.test(ua) ? 'iPhone'
    : /iPad/i.test(ua) ? 'iPad'
    : /Android/i.test(ua) ? 'Android phone'
    : /Macintosh|Mac OS X/i.test(ua) ? 'Mac'
    : /Windows/i.test(ua) ? 'Windows PC'
    : /CrOS/i.test(ua) ? 'Chromebook'
    : /Linux/i.test(ua) ? 'Linux PC'
    : 'This device'

  /*
   * The BROWSER only matters where several can hold different passkeys for the
   * same account on one machine — a desktop. On a phone it is noise: "iPhone"
   * is what somebody calls it and "iPhone (Safari)" is what a form calls it.
   */
  const desktop = ['Mac', 'Windows PC', 'Chromebook', 'Linux PC'].includes(platform)
  if (!desktop) return platform

  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : null

  return browser ? `${platform} · ${browser}` : platform
}

/**
 * Which passkeys were created in THIS browser.
 *
 * Kept locally rather than on the server, because that is genuinely what it is:
 * per-browser knowledge. The server cannot tell which of three credentials is
 * the one in front of you without recording the credential on every session,
 * and marking the wrong row "this device" is how somebody deletes the passkey
 * they are holding.
 */
const KEY = 'factory.myCredentials'

export function rememberCredential(id: string): void {
  try {
    const seen = new Set(mine())
    seen.add(id)
    localStorage.setItem(KEY, JSON.stringify([...seen]))
  } catch { /* private mode; the list simply loses its markers */ }
}

export function mine(): string[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[] } catch { return [] }
}
